import React, { useMemo } from 'react';
import { extend, withQueryProvider } from '@thd-nucleus/data-sources';
import cs from 'classnames';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useImpression } from '@thd-olt-component-react/impression';
import { withCard } from './withCard';
import { withClientSideRender } from './withClientSideRender';

export function withLayout(ComponentProp, BusinessRules, opts) {
  let Component = ComponentProp;

  function LayoutComponent(props) {
    const {
      fusionSlotName,
      fusionSlotIndex,
      fusionClassName,
      fusionComponentName,
      fusionComponentExport,
      hydratorId,
      version,
      config,
      slot,
      name,
      props: componentProps,
      skipWrapper,
      ...rest
    } = props;

    const { isDynamic, isImpressed, middleware, fusionClassName: fusionSlotCls } = config.slots[name] || {};

    const extendHOC = (Comp, Opts) => {
      // Note, this is the innermost wrapper
      if (!Comp.wraps?.queryProvider && rest.queryProvider) {
        Comp = withQueryProvider(Comp, rest.queryProvider);
      }

      if (!Comp.wraps?.hydrator) {
        if ((rest.hydrator && !Opts?.disableHydrator) || isDynamic) {
          Comp = withHydrator(
            {
              preserveCtxVal: 'clientStore',
              id: rest.hydrator?.id || hydratorId || name + '-hydrator',
              waitFor: Opts?.waitForComponent ? Opts.waitForComponent : null,
              placeholder: Opts?.placeholder,
            },
            Comp
          );
        }
      }

      if (!Comp.wraps?.dynamicComponent && rest.dynamic) {
        Comp = withDynamicComponent(Comp, { placeholder: Opts?.placeholder });
      }
      
      if (!Comp.wraps?.card && rest.card) {
        Comp = withCard(Comp, rest.card);
      }
      
      if (!Comp.wraps?.clientSideRender && rest.clientSideRender) {
        // The outer fusion-slot wrapper already applies fusionClassName/fusionSlotCls, so
        // the placeholder config is passed through as-is instead of merging those in here,
        // to avoid double-applying any padding/margin from those slot classes.
        const clientSideRenderOpts = typeof rest.clientSideRender === 'object' ? rest.clientSideRender : {};
        Comp = withClientSideRender(Comp, { placeholder: clientSideRenderOpts.placeholder });
      }
      
      // This is the outermost wrapper
      if (!Comp.wraps?.errorBoundary && rest.errorBoundary) {
        Comp = withErrorBoundary(Comp, { componentDisplayName: Comp.displayName });
      }
      return Comp;
    };

    let FirstChild;
    if (Array.isArray(Component)) {
      Component = Component.map((Comp, index) => extendHOC(Comp, Array.isArray(opts) ? opts[index] : opts)
      );
      FirstChild = Component[0];
    } else {
      Component = extendHOC(Component, opts);
      FirstChild = Component;
    }

    const impressionProps = {
      ref: null,
      clickid: null,
    };

    if (isImpressed) {
      const slotSlot = config.slots[name] || {};

      const data = {
        id: slotSlot.contentfulId || '',
        name: slotSlot.components[0]?.name || '',
        component: slotSlot.components[0]?.export || '',
        position: `${slotSlot.section}${slotSlot.slot}`,
        type: 'slot',
      };

      const { ref, clickID } = useImpression({ data });

      impressionProps.ref = ref;
      impressionProps.clickid = clickID;
    }

    if (BusinessRules) {
      return (
        <BusinessRules
          itemId={rest.itemId}
          componentDisplayName={FirstChild.displayName || FirstChild.name}
          fusionProps={rest}
          middlewareProps={{ ...middleware?.props }}
          props={{ ...rest, ...componentProps }}
          components={Array.isArray(Component) ? Component : [Component]}
        >
          {({ props: comProps, Component: BusinessRulesComp = Component }) => {
            return (
              <div
                data-fusion-slot={fusionSlotName}
                data-fusion-slot-index={fusionSlotIndex}
                data-fusion-component={fusionComponentName}
                data-fusion-export={fusionComponentExport}
                data-fusion-version={version}
                className={cs('fusion-slot', 'empty:sui-hidden', fusionClassName, fusionSlotCls, {
                  'sui-hidden': fusionSlotIndex !== undefined && fusionSlotIndex !== 0
                })}
                {...impressionProps}
              >
                {Array.isArray(BusinessRulesComp)
                  && BusinessRulesComp.map((BusinessRuleChild, index) => (
                    <BusinessRuleChild
                      key={index}
                      slot={slot}
                      name={name}
                      {...rest}
                      {...(Array.isArray(componentProps) ? componentProps[index] : componentProps)}
                      {...comProps}
                    />
                  ))}
                {!Array.isArray(BusinessRulesComp) && (
                  <BusinessRulesComp
                    slot={slot}
                    name={name}
                    {...rest}
                    {...(Array.isArray(componentProps) ? componentProps[0] : componentProps)}
                    {...comProps}
                  />
                )}
              </div>
            );
          }}
        </BusinessRules>
      );
    }

    if (Array.isArray(Component)) {
      if (FirstChild) {
        Component = FirstChild;
      } else {
        console.log(`warning, no child components found, returning null for ${name}`);
        return null;
      }
    }

    return (
      <>
        {skipWrapper ? (
          <Component slot={slot} name={name} {...rest} />
        ) : (
          <div
            data-fusion-slot={fusionSlotName}
            data-fusion-slot-index={fusionSlotIndex}
            data-fusion-export={fusionComponentExport}
            data-fusion-component={fusionComponentName}
            data-fusion-version={version}
            className={cs('fusion-slot', 'empty:sui-hidden', fusionClassName, fusionSlotCls, {
              'sui-hidden': fusionSlotIndex !== undefined && fusionSlotIndex !== 0
            })}
            {...impressionProps}
          >
            <Component slot={slot} name={name} {...rest} />
          </div>
        )}
      </>
    );
  }

  if (!Array.isArray(Component)) {
    if (opts?.displayName) {
      LayoutComponent.displayName = opts.displayName;
    } else {
      LayoutComponent.displayName = Component.displayName || Component.name;
    }
  } else if (Component?.length) {
    if (opts?.length) {
      LayoutComponent.displayName = opts[0].displayName;
    } else {
      LayoutComponent.displayName = Component[0].displayName || Component[0].name;
    }
  } else {
    console.warn('Layout component could not define display name');
  }

  let dm;
  if (Array.isArray(Component)) {
    dm = Component.map((comp) => (comp?.dataModel ? comp.dataModel : {}));
  } else {
    dm = [Component.dataModel ? Component : {}];
  }

  LayoutComponent.dataModel = extend({}, BusinessRules || {}, ...dm);

  return LayoutComponent;
}